import { convexQuery } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";
import { type ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";

import { locationSalesGetSalesByItem, type SalesByItemEntry } from "~/api-refs";
import { SalesDataTable } from "~/components/reports/sales-data-table";
import { LoadingPanel } from "~/components/shared/loading-panel";
import { toConvexTableId } from "~/lib/convex-table-id";

type ExtendedItem = SalesByItemEntry;

interface ItemViewProps {
  locationId: string;
  dateRange: {
    from: Date;
    to: Date;
  };
  tableId: string;
}

export const ItemView = ({ locationId, dateRange, tableId }: ItemViewProps) => {
  const { data, isPending } = useQuery(
    convexQuery(locationSalesGetSalesByItem, {
      dateRange: {
        from: dateRange.from.getTime(),
        to: dateRange.to.getTime(),
      },
      locationId: toConvexTableId("locations", locationId),
    })
  );

  const columnHelper = createColumnHelper<ExtendedItem>();

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor("itemName", {
          header: "Item Name",
          cell: ({ getValue }) => getValue(),
          filterFn: ({ original }, _, filterValue: string[]) =>
            filterValue.some((filter) =>
              original.itemName.toLowerCase().includes(filter.toLowerCase())
            ),
        }),
        columnHelper.accessor("orderType", {
          header: "Order Type",
          cell: ({ getValue }) => getValue(),
          filterFn: ({ original }, _, filterValue: string[]) =>
            filterValue.includes(original.orderType),
          meta: {
            faceting: {
              title: "Order Type",
              getOption: (orderType: ExtendedItem["orderType"]) => ({
                value: orderType,
                label: orderType,
              }),
            },
          },
        }),
        columnHelper.accessor("itemsSold", {
          header: "Items Sold",
          cell: ({ getValue }) => getValue().toLocaleString(),
          meta: {
            getCellForExport: (value: ExtendedItem["itemsSold"]) => value,
          },
        }),
        columnHelper.accessor("amountSold", {
          header: "Amount Sold",
          cell: ({ getValue }) =>
            getValue().toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
          meta: {
            getCellForExport: (value: ExtendedItem["amountSold"]) => value,
          },
        }),
        columnHelper.accessor("shareOfTotalSales", {
          header: "% of Total Sales",
          cell: ({ getValue }) => `${(getValue() * 100).toFixed(1)}%`,
          meta: {
            getCellForExport: (value: ExtendedItem["shareOfTotalSales"]) =>
              value,
          },
        }),
      ] as ColumnDef<ExtendedItem>[],
    [columnHelper]
  );

  if (isPending || !data) {
    return <LoadingPanel message="Loading item performance data..." />;
  }

  return <SalesDataTable columns={columns} data={data} id={tableId} />;
};
